import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Filters nav items based on the user's enabled features and roles.
 * - Items with a `feature` property are only shown when that feature is enabled.
 * - Items with a `roles` array are only shown when the current user has one of those roles.
 * - Items without either constraint are always shown.
 *
 * @param {Array<{ to: string, label: string, feature?: string, roles?: string[], matchPaths?: string[] }>} items
 * @returns {Array} Filtered nav items the current user has access to.
 */
export function useNavItems(items) {
  const { features, user } = useAuth();

  return useMemo(() => {
    const userRole = user?.role || '';
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];

    return items.filter(item => {
      // Feature gate
      if (item.feature && !(Array.isArray(features) && features.includes(item.feature))) {
        return false;
      }
      // Role gate — if `roles` is specified, user must have at least one of them
      if (item.roles && item.roles.length > 0) {
        const hasRole = item.roles.some(r => r === userRole || userRoles.includes(r));
        if (!hasRole) return false;
      }
      return true;
    });
  }, [items, features, user]);
}
